import * as _ from 'lodash';
import { Utils } from '../../Utils/Utils';
import { StringUtils } from '../../Utils/StringUtils';
import { ValidatorManager } from './ValidatorManager';

/*
 * Configuration panel item object representation.
 */
export class ConfigurationItem {
    public readonly name: string;
    public readonly parentValue: string;
    public readonly isRequired: boolean;
    public isHidden: boolean;
    public readonly isLeaf: boolean;
    public readonly isEditable: boolean;
    public readonly lockEdited: boolean;
    public readonly isText: boolean;
    public readonly code: string;
    public readonly jurisdictionOverride: boolean;
    private dtItem: HTMLElement;
    private valueItem: HTMLInputElement;

    private editItem: HTMLInputElement;
    private isSetInput: HTMLInputElement;
    private isLockedInput: HTMLElement;
    private lockItem: HTMLInputElement;
    private validations: HTMLSpanElement;
    private revealedPassword: HTMLInputElement;
    private revealButton: HTMLElement;
    private validationMessages: string[];
    private validatorManager: ValidatorManager;
    private timerId: any;
    private cannotBeEmpty: boolean;

    /*
     * Constructor - parse important elements from DOM.
     */
    public constructor(htmlItem: HTMLElement) {
        this.validationMessages = new Array();
        this.validateFieldThrottled = this.validateFieldThrottled.bind(this);
        this.validateField = this.validateField.bind(this);
        this.revealPassword = this.revealPassword.bind(this);
        this.hidePassword = this.hidePassword.bind(this);
        this.setSelectInputStyle = this.setSelectInputStyle.bind(this);
        this.dtItem = htmlItem;
        this.isHidden = htmlItem.classList.contains("hidden");
        this.name = (htmlItem.children[0] as HTMLElement).innerText;
        this.code = (htmlItem.nextElementSibling.getElementsByClassName("configurationSearchItemCode")[0] as HTMLElement).innerText;
        this.isRequired = htmlItem.getElementsByClassName("required").length > 0;
        this.valueItem = htmlItem.nextElementSibling.getElementsByClassName("configurationItemValue")[0] as HTMLInputElement;
        this.parentValue = htmlItem.dataset["parentvalue"];
        this.isLeaf = StringUtils.equals(htmlItem.dataset["leaf"], "True");
        this.lockItem = htmlItem.nextElementSibling.getElementsByClassName("isLockedValue")[0] as HTMLInputElement;
        this.editItem = htmlItem.nextElementSibling.getElementsByClassName("isSetValue")[0] as HTMLInputElement;
        this.isSetInput = htmlItem.nextElementSibling.getElementsByClassName("configuration-isset-input")[0] as HTMLInputElement;
        this.isLockedInput = htmlItem.nextElementSibling.getElementsByClassName("configuration-islocked-input")[0] as HTMLInputElement;
        this.isEditable = StringUtils.equals(htmlItem.dataset["iseditable"], "True");
        this.lockEdited = StringUtils.equals(htmlItem.dataset["lockedited"], "True");
        this.cannotBeEmpty = StringUtils.equals(htmlItem.dataset["cannotbeempty"], "True");
        this.isText = StringUtils.equals(htmlItem.dataset["istext"], "True");
        this.validations = htmlItem.nextElementSibling.getElementsByClassName("configurationItemValidation")[0] as HTMLSpanElement;
        let jurisdictionElement = htmlItem.nextElementSibling.getElementsByClassName("jurisdiction-locked-item")[0] as HTMLElement;
        this.jurisdictionOverride = !Utils.isNullOrUndefined(jurisdictionElement);
        this.validatorManager = new ValidatorManager(htmlItem);

        this.processItemTypeValue();
        this.processEditableEditItem();

        this.setValidationListeners();
        this.validateField();
    }

    private processItemTypeValue() {
        if (StringUtils.equals(this.valueItem.type, "password")) {
            this.revealedPassword = this.valueItem.nextElementSibling as HTMLInputElement;
            this.revealButton = this.valueItem.parentElement.getElementsByTagName("i")[0] as HTMLElement;
            this.revealButton.addEventListener("mousedown", this.revealPassword);
            this.revealButton.addEventListener("mouseup", this.hidePassword);
            this.revealButton.addEventListener("mouseleave", this.hidePassword);
        } else if (StringUtils.equals(this.valueItem.type, "select-one")) {
            this.valueItem.addEventListener("change", this.setSelectInputStyle);
            this.setSelectInputStyle();
        }
    }

    private processEditableEditItem() {
        if (!Utils.isNullOrUndefined(this.editItem) && this.isEditable) {
            this.valueItem.addEventListener("input", () => this.onInputChange());
            this.editItem.addEventListener("change", () => this.onIsSetChange());
            if (this.editItem.checked) {
                this.valueItem.placeholder = "";
            }
            if (!this.isText && this.isSetInput) {
                this.isSetInput.style.display = "none";
            }
            if (this.lockEdited && this.isLockedInput) {
                this.isLockedInput.style.display = "none";
            }
        }
    }

    private setSelectInputStyle() {
        if (StringUtils.isNullOrEmpty(this.getValue())) {
            this.valueItem.classList.add("configurationItemValueDefault");
        }
        else {
            this.valueItem.classList.remove("configurationItemValueDefault");
        }
    }

    private revealPassword() {
        let value = this.getValue();
        if (value === "......" || value === "") {
            this.revealedPassword.value = this.revealedPassword.dataset[value === "" ? "revealedParentValue" : "revealedValue"];
            this.revealedPassword.type = "text";
            this.valueItem.type = "hidden";
        }
        else {
            this.valueItem.type = "text";
        }
    }

    private hidePassword() {
        this.revealedPassword.type = "hidden";
        this.valueItem.type = "password";
    }

    // Set validation listener on input change.
    private setValidationListeners(): void {
        this.valueItem.addEventListener("input", this.validateFieldThrottled);
        if (!Utils.isNullOrUndefined(this.lockItem)) {
            this.lockItem.addEventListener("input", this.validateFieldThrottled);
        }
    }

    private validateFieldThrottled() {
        if (this.timerId) {
            return;
        }
        var func = this.validateField;
        this.timerId = setTimeout(function(){
            func();
        }, 250)
    }

    // Field validation.
    public validateField() {
        this.validationMessages = [];
        const value = this.getValue();
        const isValueEmpty = StringUtils.isNullOrEmpty(value);
        this.validations.innerHTML = "";
        const isSet = Utils.isNullOrUndefined(this.editItem) ? false : this.editItem.checked;
        if (this.isRequired && isValueEmpty && (StringUtils.isNullOrEmpty(this.parentValue) || isSet)) {
            this.validationMessages.push("This field is required");
        }
        if (isValueEmpty && this.isLocked() && (!this.isText || (this.isText && !this.editItem.checked))) {
            this.validationMessages.push("Only explicit value can be locked");
        }
        if (isValueEmpty && !this.isRequired && isSet && this.cannotBeEmpty) {
            this.validationMessages.push("This field cannot be set to an empty value");
        }
        this.validatorManager.validate(value, this.validationMessages);
        for (let i = 0; i < this.validationMessages.length; i++) {
            let validationMessage = this.validationMessages[i];
            let validationMessageItem = document.createElement("li");
            validationMessageItem.innerText = validationMessage;
            this.validations.appendChild(validationMessageItem);
        }
        this.timerId = undefined;
    }

    private lockEditedItem() {
        if (!Utils.isNullOrUndefined(this.lockItem) && this.lockEdited) {
            if (this.editItem.checked) {
                this.lockItem.checked = true;
            } else {
                this.lockItem.checked = false;
            }
        }
    }

    public onInputChange() {
        if (Utils.isNullOrUndefined(this.editItem)) {
            return false;
        }
        if (this.valueItem.value == '') {
            this.valueItem.placeholder = this.parentValue;
            this.editItem.checked = false;
        } else {
            this.editItem.checked = true;
        }
        this.lockEditedItem();
    }

    public onIsSetChange() {
        if (Utils.isNullOrUndefined(this.valueItem)) {
            return false;
        }
        if (this.editItem.checked) {
            this.valueItem.placeholder = "";
        } else {
            this.valueItem.placeholder = this.parentValue;
        }
        this.valueItem.value = "";
        this.lockEditedItem();
        this.validateField();
    }

    public isSet(): boolean {
        return !Utils.isNullOrUndefined(this.editItem) && this.editItem.checked;
    }

    // Hide configuration item.
    public hide() {
        this.isHidden = true;
        this.dtItem.classList.add("hidden");
    }

    // Show configuration item.
    public show() {
        this.isHidden = false;
        this.dtItem.classList.remove("hidden");
    }

    // Get explicit value of item.
    public getValue(): string {
        if (this.valueItem.tagName === "LABEL") {
            return this.valueItem.innerText;
        }
        return this.valueItem.value;
    }

    // Get lock status.
    public isLocked(): boolean {
        if (Utils.isNullOrUndefined(this.lockItem)) {
            return false;
        }
        return this.lockItem.checked;
    }

    // Get validity status.
    public isValid(): boolean {
        return this.validationMessages.length === 0;
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Components/ConfigurationPanel/ConfigurationItem.ts
 **/